import React, { useEffect, useState, useContext } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import socketIOClient from "socket.io-client";
import marginTopAllComponent from "../../utils/styleGlobal";
import { Card, Col, Row, Form } from "react-bootstrap";
import {
  FaWifi,
  FaDesktop,
  FaEthernet,
  FaBolt,
  FaUserAlt,
  FaTimesCircle,
  FaArrowDown,
  FaArrowUp,
  FaInfoCircle,
} from "react-icons/fa";
import { Modal } from "react-bootstrap";
import UserService from "../../services/userServices";
import "../../assets/style/usageNowPage.css";
import Loader from "../../components/loader/loader";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from "recharts";

import UsageServices from "../../services/usageServices";
import { generateDate, formatLastUpdate } from "../../utils/formatDate.js";
import dayjs from "dayjs";

// Formateur de Mo
//const formatYAxis = (value) => `${(value / 1024).toFixed(3)} Mo`;

//Formateur en Ko
const formatYAxis = (value) => `${(value).toFixed(1)} Ko`;

// Couleurs des courbes
const lineStyles = {
  entrant: { color: "#28a745", label: "Débit entrant", icon: <FaArrowDown /> },
  sortant: { color: "#dc3545", label: "Débit sortant", icon: <FaArrowUp /> },
  consommation: { color: "#007bff", label: "Consommation", icon: <FaBolt /> },
};

const UsageNowPage = ({ setAlert }) => {
  const { isLogged } = useContext(AuthContext);
  const [usages, setUsages] = useState([]);
  const [users, setUsers] = useState([]);
  const [filterName, setFilterName] = useState("");
  const [filterComputer, setFilterComputer] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingUser, setIsLoadingUser] = useState(true);
  const [syncStartTimes, setSyncStartTimes] = useState({});
  const [now, setNow] = useState(Date.now());
  const [selectedUsage, setSelectedUsage] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [visibleLines, setVisibleLines] = useState({
    entrant: true,
    sortant: true,
    consommation: true,
  });
  const [networkData, setNetworkData] = useState([]);
  const [selectedHour, setSelectedHour] = useState(dayjs().format("HH"));
  const [offlineData, setOfflineData] = useState({});

  const toggleLine = (key) => {
    setVisibleLines((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const ENDPOINT = process.env.REACT_APP_SOCKET || "http://localhost:4000";

  const role3Users = users.filter((user) => user.role === 3);

  const filteredUsers = role3Users
    .filter((user) => {
      const usage = usages.find((u) => u.idUser === user._id);
      const fullName = `${user.nom} ${user.prenom}`.toLowerCase();

      const matchesName = fullName.includes(filterName.toLowerCase());
      const matchesComputer = usage
        ? usage.computerName
            .toLowerCase()
            .includes(filterComputer.toLowerCase())
        : filterComputer === "";

      const matchesStatus =
        filterStatus === "all" ||
        (filterStatus === "online" && usage) ||
        (filterStatus === "offline" && !usage);

      return matchesName && matchesComputer && matchesStatus;
    })
    .sort((a, b) => {
      const isAOnline = usages.some((u) => u.idUser === a._id);
      const isBOnline = usages.some((u) => u.idUser === b._id);

      return isAOnline === isBOnline ? 0 : isAOnline ? -1 : 1;
    });

  useEffect(() => {
    setIsLoadingUser(true);
    UserService.getAll()
      .then((res) => {
        setUsers(res.data.data);
        setIsLoadingUser(false);
      })
      .catch((err) => {
        console.error("Erreur:", err);
        setIsLoadingUser(false);
      });
  }, [isLogged]);

  useEffect(() => {
    const interval = setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const socket = socketIOClient(ENDPOINT);
    setIsLoading(true);
    socket.on("usageData", (usageData) => {
      setUsages((prevUsages) => {
        const updated = [...prevUsages];
        const index = updated.findIndex(
          (u) => u.computerName === usageData.computerName
        );
        if (index !== -1) {
          updated[index] = usageData;
        } else {
          updated.push(usageData);
        }
        return updated;
      });
    });

    socket.on("userDisconnected", (disconnectedUserId) => {
      setUsages((prevUsages) =>
        prevUsages.filter((usage) => usage.idUser !== disconnectedUserId)
      );
    });

    setIsLoading(false);

    return () => {
      socket.disconnect();
    };
  }, []);

  useEffect(() => {
    const now = Date.now();
    const newSyncTimes = {};

    filteredUsers.forEach((user) => {
      if (
        !syncStartTimes[user._id] &&
        !usages.find((u) => u.idUser === user._id)
      ) {
        if (!offlineData[user._id]) {
          getInfoOffline(user._id);
        }
        newSyncTimes[user._id] = now;
      }
    });

    if (Object.keys(newSyncTimes).length > 0) {
      setSyncStartTimes((prev) => ({ ...prev, ...newSyncTimes }));
    }
  }, [filteredUsers, usages]);

  useEffect(() => {
    if (selectedUsage?._doc?.dataParMinute?.length > 0) {
      const parsed = selectedUsage._doc.dataParMinute.map((entry) => ({
        minute: dayjs(entry.temps).format("HH:mm"),
        entrant: entry.rxMo || 0,
        sortant: entry.txMo || 0,
        consommation: entry.data || 0,
      }));
      setNetworkData(parsed);
      setSelectedHour(dayjs().format("HH"));
    } else {
      setNetworkData([]);
    }
  }, [selectedUsage]);

  const formatDataUsage = (mo) => {
    if (!mo && mo !== 0) return "N/A";
    if (mo >= 1024) {
      return (mo / 1024).toFixed(2) + " Go";
    }
    return mo + " Mo";
  };

  const formatBytesPerSecond = (bytes, seconds) => {
    if (seconds === 0) return "0 o/s";
    const bps = bytes / seconds;
    if (bps >= 1024 * 1024) {
      return isNaN((bps / (1024 * 1024)).toFixed(2))
        ? "0 o/s"
        : (bps / (1024 * 1024)).toFixed(2) + "Mo/s";
    } else if (bps >= 1024) {
      return isNaN((bps / 1024).toFixed(2))
        ? "0 o/s"
        : (bps / 1024).toFixed(2) + " Ko/s";
    } else {
      return isNaN(bps.toFixed(2)) ? "0 o/s" : bps.toFixed(2) + " o/s";
    }
  };

  const handleCardClick = (usage) => {
    setSelectedUsage(usage);
    setShowModal(true);
  };

  const filteredData = networkData.filter((entry) => {
    if (!selectedHour) return true;
    const hour = entry.minute.split(":")[0];
    return hour === selectedHour;
  });

  const getInfoOffline = async (idUser) => {
    const data = {
      idUser,
      daylyDataUsed: generateDate(new Date()),
    };
    try {
      const res = await UsageServices.getInfoOffline(data);
      if (res.data) {
        console.log(res.data);

        setOfflineData((prev) => ({
          ...prev,
          [idUser]: res.data,
        }));
      } else {
        setOfflineData((prev) => ({
          ...prev,
          [idUser]: null,
        }));
      }
    } catch (error) {
      console.error(error);
      setOfflineData((prev) => ({
        ...prev,
        [idUser]: null,
      }));
    }
  };

  return (
    <>
      <div className="container" style={{ marginTop: marginTopAllComponent() }}>
        <h1 className="mb-4 text-dark text-center">Consommation actuelle</h1>

        {isLoading || isLoadingUser ? (
          <Loader />
        ) : (
          <>
            {/* Filtres */}
            <Form className="mb-4">
              <Row>
                <Col md={4} className="mb-2">
                  <Form.Control
                    type="text"
                    placeholder="🔍 Nom utilisateur"
                    value={filterName}
                    onChange={(e) => setFilterName(e.target.value)}
                  />
                </Col>
                <Col md={4} className="mb-2">
                  <Form.Control
                    type="text"
                    placeholder="🔍 Nom de l'ordinateur"
                    value={filterComputer}
                    onChange={(e) => setFilterComputer(e.target.value)}
                  />
                </Col>
                <Col md={4} className="mb-2">
                  <Form.Select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                  >
                    <option value="all">🔁 Tous les statuts</option>
                    <option value="online">🟢 En ligne</option>
                    <option value="offline">🔴 Hors ligne</option>
                  </Form.Select>
                </Col>
              </Row>
            </Form>

            {/* Affichage des cartes */}
            {isLogged ? (
              <Row className="real-time-container">
                {filteredUsers.length > 0 ? (
                  filteredUsers.map((user, index) => {
                    const usage = usages.find((u) => u.idUser === user._id);
                    return (
                      <Col sm={12} md={6} lg={4} key={index} className="mb-4">
                        <Card
                          className={`shadow-lg cursor-pointer futuristic-card ${
                            usage ? "online-card" : "offline-card"
                          }`}
                          border="dark"
                          onClick={() => handleCardClick(usage)}
                          style={{ cursor: "pointer" }}
                        >
                          <Card.Body>
                            <div
                              className={`status-badge ${
                                usage ? "status-online" : "status-offline"
                              } mb-2`}
                            >
                              {usage ? "🟢 En ligne" : "🔴 Hors ligne"}
                            </div>
                            <Card.Title className="mb-2 mt-2 futuristic-card-text">
                              <FaUserAlt className="me-2" />
                              {user.nom} {user.prenom}
                            </Card.Title>

                            {usage ? (
                              <>
                                <Card.Title className="mb-2 mt-4 futuristic-card-title">
                                  <FaDesktop className="me-2" />{" "}
                                  {usage.computerName}
                                </Card.Title>
                                <Card.Subtitle className="mb-2 mt-2 futuristic-card-subtitle">
                                  {usage.connectionType === "wireless" ||
                                  usage.connectionType === "wired" ? (
                                    <FaWifi />
                                  ) : (
                                    <FaEthernet />
                                  )}
                                  <span className="ms-2">
                                    {usage.connectionType === "wireless" ||
                                    usage.connectionType === "wired"
                                      ? "Wifi"
                                      : "Ethernet"}{" "}
                                    {usage?.ssid && usage.ssid != "Inconnu"
                                      ? `(${usage.ssid})`
                                      : ""}
                                  </span>
                                </Card.Subtitle>
                                <Card.Title className="mt-4 futuristic-card-text">
                                  <strong style={{ marginBottom: "0" }}>
                                    Consommation :
                                  </strong>
                                  <span className="animated-data">
                                    {formatDataUsage(usage.dataUsed)}
                                  </span>
                                  <FaBolt className="animated-data-icon" />
                                </Card.Title>
                                <Card.Title className="mt-4 futuristic-card-text">
                                  <strong>Débit :</strong>
                                  <div
                                    className="d-flex justify-content-between align-items-center mt-1 px-2 py-1 text-light"
                                    style={{ fontSize: "1rem" }}
                                  >
                                    <div
                                      className="d-flex align-items-center text-success"
                                      style={{ gap: "4px" }}
                                    >
                                      <FaArrowDown />
                                      <span>
                                        {formatBytesPerSecond(
                                          usage?.debitEntrant,
                                          usage?.secondsElapsed
                                        )}
                                      </span>
                                    </div>
                                    <div className="mx-2">
                                      <strong>|</strong>
                                    </div>
                                    <div
                                      className="d-flex align-items-center text-danger"
                                      style={{ gap: "4px" }}
                                    >
                                      <FaArrowUp />
                                      <span>
                                        {formatBytesPerSecond(
                                          usage?.debitSortant,
                                          usage?.secondsElapsed
                                        )}
                                      </span>
                                    </div>
                                  </div>
                                </Card.Title>
                                <Card.Subtitle
                                  className="mb-2 mt-4 futuristic-card-subtitle"
                                  style={{ fontSize: "0.9rem" }}
                                >
                                  Connecté depuis:{" "}
                                  {formatLastUpdate(
                                    usage?._doc?.createdAt,
                                    false
                                  )}
                                </Card.Subtitle>
                              </>
                            ) : (
                              (() => {
                                const syncStart =
                                  syncStartTimes[user._id] || Date.now();
                                const isSyncing =
                                  !usage && now - syncStart < 120000;
                                const offlineInfo = offlineData[user._id];
                                // console.log(offlineData);

                                return isSyncing ? (
                                  <div className="offline-info d-flex flex-column align-items-center justify-content-center text-center h-100">
                                    <div
                                      className="text-warning mb-2"
                                      style={{ fontSize: "1.2rem" }}
                                    >
                                      <span className="animated-icon">⏳</span>{" "}
                                      En cours de synchronisation...
                                    </div>
                                    <div className="offline-subtitle">
                                      Veuillez patienter
                                      <span className="animated-dots"></span>
                                    </div>
                                  </div>
                                ) : offlineInfo ? (
                                  <>
                                    <Card.Title className="mb-2 mt-4 futuristic-card-title">
                                      <FaDesktop className="me-2" />{" "}
                                      {offlineInfo.computerName}
                                    </Card.Title>
                                    <Card.Subtitle className="mb-2 mt-2 futuristic-card-subtitle">
                                      {offlineInfo.connectionType ===
                                        "wireless" ||
                                      offlineInfo.connectionType === "wired" ? (
                                        <FaWifi />
                                      ) : (
                                        <FaEthernet />
                                      )}
                                      <span className="ms-2">
                                        {offlineInfo.connectionType ===
                                          "wireless" ||
                                        offlineInfo.connectionType === "wired"
                                          ? "Wifi"
                                          : "Ethernet"}{" "}
                                        {usage?.ssid &&
                                        offlineInfo.ssid != "Inconnu"
                                          ? `(${offlineInfo.ssid})`
                                          : ""}
                                      </span>
                                    </Card.Subtitle>
                                    <Card.Title className="mt-4 futuristic-card-text">
                                      <strong style={{ marginBottom: "0" }}>
                                        Consommation :
                                      </strong>
                                      <span className="animated-data">
                                        {formatDataUsage(offlineInfo.dataUsed)}
                                      </span>
                                    </Card.Title>
                                    <Card.Title className="mt-4 futuristic-card-text">
                                      <strong>Débit :</strong>
                                      <div
                                        className="d-flex justify-content-between align-items-center mt-1 px-2 py-1 text-light"
                                        style={{ fontSize: "1rem" }}
                                      >
                                        <div
                                          className="d-flex align-items-center text-success"
                                          style={{ gap: "4px" }}
                                        >
                                          <FaArrowDown />
                                          <span>
                                            {formatBytesPerSecond(
                                              offlineInfo?.debitEntrant,
                                              offlineInfo?.secondsElapsed
                                            )}
                                          </span>
                                        </div>
                                        <div className="mx-2">
                                          <strong>|</strong>
                                        </div>
                                        <div
                                          className="d-flex align-items-center text-danger"
                                          style={{ gap: "4px" }}
                                        >
                                          <FaArrowUp />
                                          <span>
                                            {formatBytesPerSecond(
                                              offlineInfo?.debitSortant,
                                              offlineInfo?.secondsElapsed
                                            )}
                                          </span>
                                        </div>
                                      </div>
                                    </Card.Title>
                                    <Card.Subtitle
                                      className="mb-2 mt-4 futuristic-card-subtitle"
                                      style={{ fontSize: "0.9rem" }}
                                    >
                                      Dernière connexion :{" "}
                                      {formatLastUpdate(
                                        offlineInfo.updatedAt,
                                        true
                                      )}
                                    </Card.Subtitle>
                                  </>
                                ) : (
                                  <>
                                    <Card.Title className="mb-2 mt-4 futuristic-card-title">
                                      <FaDesktop className="me-2" /> Inconnu
                                    </Card.Title>
                                    <Card.Subtitle className="mb-2 mt-2 futuristic-card-subtitle">
                                      <span className="ms-2">🔌 Inconnu</span>
                                    </Card.Subtitle>
                                    <Card.Title className="mt-4 futuristic-card-text">
                                      <strong style={{ marginBottom: "0" }}>
                                        Consommation :
                                      </strong>
                                      <span className="animated-data">
                                        0 Mo
                                      </span>
                                    </Card.Title>
                                    <Card.Title className="mt-4 futuristic-card-text">
                                      <strong>Débit :</strong>
                                      <div
                                        className="d-flex justify-content-between align-items-center mt-1 px-2 py-1 text-light"
                                        style={{ fontSize: "1rem" }}
                                      >
                                        <div
                                          className="d-flex align-items-center text-success"
                                          style={{ gap: "4px" }}
                                        >
                                          <FaArrowDown />
                                          <span>0 o/s</span>
                                        </div>
                                        <div className="mx-2">
                                          <strong>|</strong>
                                        </div>
                                        <div
                                          className="d-flex align-items-center text-danger"
                                          style={{ gap: "4px" }}
                                        >
                                          <FaArrowUp />
                                          <span>0 o/s</span>
                                        </div>
                                      </div>
                                    </Card.Title>
                                    <Card.Subtitle
                                      className="mb-2 mt-4 futuristic-card-subtitle"
                                      style={{ fontSize: "0.9rem" }}
                                    >
                                      Dernière connexion : Plus d'un jour
                                    </Card.Subtitle>
                                  </>
                                );
                              })()
                            )}
                          </Card.Body>
                        </Card>
                      </Col>
                    );
                  })
                ) : (
                  <p className="text-center text-warning futuristic-text">
                    Aucun utilisateur ne correspond aux filtres.
                  </p>
                )}
              </Row>
            ) : (
              <p className="text-danger text-center mt-4 futuristic-text">
                Vous devez être connecté pour voir la liste des utilisateurs.
              </p>
            )}
          </>
        )}
        {selectedUsage && (
          <Modal
            show={showModal}
            onHide={() => setShowModal(false)}
            size="xl"
            centered
            backdrop="static"
            className="modern-modal"
          >
            <Modal.Header
              closeButton
              style={{
                background: "#f5f7fa",
                borderBottom: "1px solid #dee2e6",
              }}
            >
              <Modal.Title
                style={{
                  fontWeight: "bold",
                  fontSize: "1.5rem",
                  color: "#343a40",
                }}
              >
                📊 Informations détailléess
              </Modal.Title>
            </Modal.Header>

            <Modal.Body style={{ backgroundColor: "#ffffff", padding: "2rem" }}>
              <div style={{ marginBottom: "1.5rem" }}>
                <h5 style={{ fontWeight: "600", color: "gray" }}>
                  💻 Ordinateur :{" "}
                  <span style={{ color: "gray" }}>
                    {selectedUsage.computerName}
                  </span>
                </h5>
                <p style={{ marginBottom: "0.5rem", color: "#495057" }}>
                  🔌 Connexion :{" "}
                  <span style={{ fontWeight: "500", color: "gray" }}>
                    {selectedUsage.connectionType === "wireless"
                      ? "Wifi"
                      : "Ethernet"}{" "}
                    {selectedUsage?.ssid && selectedUsage.ssid != "Inconnu"
                      ? `(${selectedUsage.ssid})`
                      : ""}
                  </span>
                </p>
                <p style={{ color: "#495057" }}>
                  ⚡ Consommation :{" "}
                  <span style={{ fontWeight: "500", color: "#007bff" }}>
                    {formatDataUsage(selectedUsage.dataUsed)}
                  </span>
                </p>
              </div>

              <div className="mb-3">
                <label
                  htmlFor="hourFilter"
                  style={{ fontWeight: 500, marginRight: "10px" }}
                >
                  🕐 Filtrer par heure :
                </label>
                <select
                  id="hourFilter"
                  value={selectedHour}
                  onChange={(e) => setSelectedHour(e.target.value)}
                  style={{ padding: "6px 10px", borderRadius: "6px" }}
                >
                  <option value="">Toutes</option>
                  {Array.from({ length: 24 }, (_, i) => (
                    <option key={i} value={i.toString().padStart(2, "0")}>
                      {i.toString().padStart(2, "0")}h
                    </option>
                  ))}
                </select>
              </div>

              <div className="chart-legends">
                {Object.keys(lineStyles).map((key) => (
                  <div
                    key={key}
                    onClick={() => toggleLine(key)}
                    className={`legend-item ${
                      visibleLines[key] ? "visible" : "hidden"
                    }`}
                    style={{ color: lineStyles[key].color }}
                  >
                    <span
                      className="legend-icon"
                      style={{ backgroundColor: lineStyles[key].color }}
                    ></span>
                    <span>{lineStyles[key].label}</span>
                  </div>
                ))}
              </div>

              <ResponsiveContainer width="100%" height={320}>
                {filteredData.length > 0 ? (
                  <LineChart
                    data={filteredData}
                    margin={{ top: 10, right: 30, bottom: 10, left: 60 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis dataKey="minute" stroke="#6c757d" />
                    <YAxis stroke="#6c757d" tickFormatter={formatYAxis} />
                    <Tooltip
                      formatter={(value) => `${(value).toFixed(1)} Ko`}
                      labelStyle={{ fontWeight: "bold", color: "#343a40" }}
                      contentStyle={{
                        backgroundColor: "#fff",
                        borderColor: "#dee2e6",
                        borderRadius: "8px",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                      }}
                    />
                    {visibleLines.entrant && (
                      <Line
                        type="monotone"
                        dataKey="entrant"
                        name="Débit entrant"
                        stroke={lineStyles.entrant.color}
                        strokeWidth={2}
                        strokeDasharray="8 4"
                        strokeOpacity={0.8}
                        dot={{ r: 3, stroke: lineStyles.entrant.color }}
                      />
                    )}
                    {visibleLines.sortant && (
                      <Line
                        type="monotone"
                        dataKey="sortant"
                        name="Débit sortant"
                        stroke={lineStyles.sortant.color}
                        strokeWidth={2}
                        strokeDasharray="8 4"
                        strokeOpacity={0.8}
                        dot={{ r: 3, stroke: lineStyles.sortant.color }}
                      />
                    )}
                    {visibleLines.consommation && (
                      <Line
                        type="monotone"
                        dataKey="consommation"
                        name="Consommation"
                        stroke={lineStyles.consommation.color}
                        strokeWidth={3}
                        strokeOpacity={1}
                        filter="drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.3))"
                        dot={{ r: 5, stroke: lineStyles.consommation.color }}
                      />
                    )}
                  </LineChart>
                ) : (
                  <div
                    style={{
                      textAlign: "center",
                      paddingTop: "100px",
                      color: "#6c757d",
                    }}
                  >
                    Aucune donnée pour cette heure.
                  </div>
                )}
              </ResponsiveContainer>
            </Modal.Body>
          </Modal>
        )}
      </div>
      <style>
        {`
        .modern-modal .modal-content {
          border-radius: 12px;
          box-shadow: 0 0 20px rgba(0, 0, 0, 0.1);
        }

      `}
      </style>
    </>
  );
};

export default UsageNowPage;
